import React, { useState, useEffect } from 'react'
import { useDispatch, connect } from 'react-redux'
import RootState from 'typings/RootState'
import styled from 'styled-components'
import { RouteComponentProps } from 'react-router-dom'

import { BreadcrumbLink } from 'Components/Shared/BreadCrumb/BreadCrumb'
import SubnavBar from 'Components/Shared/SubnavBar'
import { HeaderRow } from 'Components/Shared/Table'
import Pagination from 'Components/Shared/Pagination'

import { PaginationType } from 'features/types/common'

import {
  userEmailSelector,
  serviceTokenSelector,
} from 'features/sensitive/sensitive.selector'

import {
  getReportList,
  exportReportList,
} from 'features/adminReport/commonArea/api'
import { sortingTask } from 'features/adminReport/commonArea/actions'
import {
  GetReportListReq,
  DataItem,
} from 'features/adminReport/commonArea/types'

import Menu from 'Components/MenuAdmin/AdminQCMenu'

import RowItem from './RowItem'
import { EventModal, ReportZoneTab } from 'Components/Shared'
import CommonAreaFilter from 'Components/AdminQC/Report/Filter/CommonAreaFilter'
import { toDateText } from 'utils/datetime'
import { initPagination } from 'utils/helper'

const breadcrumbs: BreadcrumbLink[] = [
  {
    url: '/dashboard',
    title: 'Home',
    icon: '/home.png',
    active: true,
    showTitle: false,
  },
  {
    url: '/report/zone',
    title: 'รายงานตรวจส่วนกลาง',
    active: false,
    showTitle: true,
  },
]

type Props = ReduxProps & DispatchProps & RouteComponentProps

const ReportCommissionList: React.ComponentType<Props> = ({
  UserEmail,
  token,

  dataList,
  event,
  history,
  filter: currentFilter,
  initFilter,
  sorting,
  dataExport,
  exportEvent,
}) => {
  const dispatch = useDispatch()
  const [enableModal, setEnableModal] = useState(false)
  const [pagination, setPagination] = useState<PaginationType>(initPagination())

  useEffect(() => {
    onFindReport()
  }, [])

  useEffect(() => {
    onFindReport()
  }, [pagination])

  const onFindReport = (isClearFilter?: boolean, isExport: boolean = false) => {
    setEnableModal(true)
    const filter = isClearFilter ? initFilter : currentFilter
    const req: GetReportListReq = {
      UserEmail,
      ProjectTypeID: filter.ProjectTypeID,
      StartDate: toDateText(filter.Date.StartDate || ''),
      EndDate: toDateText(filter.Date.FinishDate || ''),
      ProjectCode: filter.ProjectCode,
      FloorList: filter.FloorList,
      UnitList: filter.ZoneList,
      TowerList: filter.TowerList,
      Page: isExport ? 1 : pagination.Page,
      RowPerPage: isExport ? dataList.Total : pagination.RowPerPage,
      ExportType: isExport ? 'raw' : '',
    }
    if (isExport) {
      dispatch(exportReportList(token, req))
    } else {
      dispatch(getReportList(token, req))
    }
  }

  const onSuccess = () => {
    setEnableModal(false)
  }

  const onSort = (name: string) => {
    dispatch(sortingTask({ name }))
  }

  const content = dataList.List.map((item: DataItem, index: number) => {
    return <RowItem key={index} item={item} />
  })

  return (
    <>
      <SubnavBar breadcrumbs={breadcrumbs} hideSearch={true} />
      <EventModal
        name="report-commissoning"
        event={[event, exportEvent]}
        enable={enableModal}
        successCalback={onSuccess}
      />
      <Menu selected={'report.zone'}>
        <MainPage>
          <Header>รายงานตรวจส่วนกลาง</Header>
          <ReportZoneTab selected="CommonArea" />
          <CommonAreaFilter
            onSearch={(isClear: boolean = false) => {
              pagination.Page = 1
              setPagination({ ...pagination })
              onFindReport(isClear)
            }}
            isDownLoading={exportEvent.IsLoading}
            onExport={() => onFindReport(false, true)}
            exportData={dataExport}
          />
          <HeaderRow
            onSort={name => onSort(name)}
            sorting={sorting}
            items={columnsName}
          />
          {content}
          <Pagination
            currentPage={pagination.Page}
            totalRows={dataList.Total}
            perPage={pagination.RowPerPage}
            onChangeNumber={(page: number) => {
              pagination.RowPerPage = page
              setPagination({ ...pagination })
            }}
            onClickPage={(page: number) => {
              pagination.Page = page
              setPagination({ ...pagination })
            }}
          />
        </MainPage>
      </Menu>
    </>
  )
}

const MainPage = styled.div`
  padding: 40px 45px;
  width: 100%;
`

const Header = styled.div`
  margin-bottom: 27px;
  font-size: 28px;
  line-height: 1.07;
  font-family: 'DB Helvethaica X 75 Bd';
  color: #000;
`

const columnsName = [
  {
    title: 'อาคาร',
    span: 2,
    key: 'Tower',
  },
  {
    title: 'ชั้น',
    span: 2,
    key: 'Floor',
  },
  {
    title: 'โซน',
    span: 3,
    key: 'Unit',
  },
  {
    title: 'ครั้งที่ตรวจ',
    span: 2,
    key: 'CheckNumber',
  },
  {
    title: 'วันที่ส่งรายงาน',
    span: 2,
    key: 'CheckedDate',
  },
  {
    title: 'ไม่ผ่าน(CM)',
    span: 2,
    key: 'NotPassByCM',
  },
  {
    title: 'ผ่าน(CM)',
    span: 2,
    key: 'PassByCM',
  },
  {
    title: 'ไม่ผ่าน(QC1)',
    span: 2,
    key: 'NotPassByQC1',
  },
  {
    title: 'ผ่าน(QC1)',
    span: 2,
    key: 'PassByQC1',
  },
  {
    title: 'ไม่ผ่าน(QC2)',
    span: 2,
    key: 'NotPassByQC2',
  },
  {
    title: 'ผ่าน(QC2)',
    span: 1,
    key: 'PassByQC2',
  },
  {
    title: 'ไม่ผ่าน CM+QC1 (SUM)',
    span: 1,
    key: 'NumOfFail',
  },
  {
    title: 'ผ่าน CM+QC1 (SUM)',
    span: 1,
    key: 'NumOfPass',
  },
]

const mapStateToProps = (state: RootState) => {
  return {
    dataList: state.adminReportCommonArea.DataList,
    event: state.adminReportCommonArea.Event,
    filter: state.adminReportCommonArea.Filter,
    initFilter: state.adminReportCommonArea.ClearFilter,
    sorting: state.adminReportCommonArea.sorting,
    UserEmail: userEmailSelector(state),
    token: serviceTokenSelector(state),
    dataExport: state.adminReportCommonArea.DataExport,
    exportEvent: state.adminReportCommonArea.ExportEvent,
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>
const mapDispatchToProps = {}

type DispatchProps = typeof mapDispatchToProps

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ReportCommissionList)
